import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * P9 — rake per settlement day, split into the part that is real revenue and
 * the part that is the house raking its own float.
 *
 * Same `humans ÷ seats` rule as P3, from the same shared proration, at a day
 * grain instead of a composition one. Bucketed on the rake movement's own
 * `createdAt`: the ledger timestamp IS settlement time, and it keeps the same
 * clock every other money figure on this dashboard uses.
 *
 * `realUserRevenueUsdt` and `rakeUsdt` are rounded so that the days sum to
 * `realUserTotalUsdt` and `totalUsdt` exactly — the same figures P3 reports for
 * the window, so the two panels cannot quote different revenue.
 *
 * A day with no rake is absent, never a zero row.
 */
export const ArenaOpsRevenueDaySchema = z.object({
  /** UTC midnight opening the bucket. */
  day: z.iso.datetime(),
  /** All rake settled that day, the unattributed bucket included. */
  rakeUsdt: ArenaOpsUsdtSchema,
  /** The `humans ÷ seats` share — the revenue figure. */
  realUserRevenueUsdt: ArenaOpsUsdtSchema,
  /** `rakeUsdt − realUserRevenueUsdt`, served rather than subtracted in the browser. */
  houseFundedUsdt: ArenaOpsUsdtSchema,
  movementCount: z.number().int(),
});

export const ArenaOpsMoneyRevenuePerDayResponseSchema = z.object({
  days: z.array(ArenaOpsRevenueDaySchema),
  totalUsdt: ArenaOpsUsdtSchema,
  realUserTotalUsdt: ArenaOpsUsdtSchema,
  houseFundedTotalUsdt: ArenaOpsUsdtSchema,
  /** Rake whose key resolves to no proposal. Counted in `rakeUsdt`, never in revenue. */
  unattributedUsdt: ArenaOpsUsdtSchema,
  unattributedMovementCount: z.number().int(),
});
export type ArenaOpsMoneyRevenuePerDayResponse = z.infer<typeof ArenaOpsMoneyRevenuePerDayResponseSchema>;
